// Pure SOW filter — search + status + date-range + include-archived.
// Mirrors the deliverable / meeting `applyChipFilters` pattern: the
// caller composes `filters + search + today`, this returns the matching
// subset. No mutation, no derived state.
//
// Search matches against `name` and `number`. Date range is evaluated
// against the SOW's `startDate` (the first event on its lifecycle).
// Cancelled SOWs are kept in the filter — the user can still want to
// see them in the list; status chips are how they're filtered out.

import type { Sow, SowStatus } from "../../../../types";

export type SowDateRangeFilter =
  | "next-30-days"
  | "past-30-days"
  | "this-quarter"
  | "all-time";

export interface SowFilters {
  status: SowStatus[];
  dateRange: SowDateRangeFilter;
  includeArchived: boolean;
}

const DAY_MS = 86_400_000;

export function emptySowFilters(): SowFilters {
  return {
    status: [],
    dateRange: "all-time",
    includeArchived: false,
  };
}

function startOfUTCDay(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function isoToUTCDay(iso: string): number | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function matchesDateRange(
  sow: Sow,
  range: SowDateRangeFilter,
  today: Date,
): boolean {
  if (range === "all-time") return true;
  const ms = isoToUTCDay(sow.startDate);
  if (ms === null) return false;
  const todayMs = startOfUTCDay(today);
  if (range === "next-30-days") {
    return ms >= todayMs && ms <= todayMs + 30 * DAY_MS;
  }
  if (range === "past-30-days") {
    return ms < todayMs && ms >= todayMs - 30 * DAY_MS;
  }
  if (range === "this-quarter") {
    const month = today.getUTCMonth();
    const qStartMonth = Math.floor(month / 3) * 3;
    const qStart = Date.UTC(today.getUTCFullYear(), qStartMonth, 1);
    const qEnd = Date.UTC(today.getUTCFullYear(), qStartMonth + 3, 0);
    return ms >= qStart && ms <= qEnd;
  }
  return true;
}

export function applySowFilters(
  sows: readonly Sow[],
  filters: SowFilters,
  search: string,
  today: Date = new Date(),
): Sow[] {
  const q = search.trim().toLowerCase();
  return sows.filter((sow) => {
    if (!filters.includeArchived && sow.archivedAt) return false;
    if (filters.status.length > 0 && !filters.status.includes(sow.status)) {
      return false;
    }
    if (!matchesDateRange(sow, filters.dateRange, today)) return false;
    if (q) {
      const inName = sow.name.toLowerCase().includes(q);
      const inNumber = sow.number.toLowerCase().includes(q);
      if (!inName && !inNumber) return false;
    }
    return true;
  });
}
